/* eslint-disable */
import React from 'react'
import { connect } from 'react-redux'
import {
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Input,
  message,
  Popconfirm,
  Row,
  Space,
  Spin,
  Tag,
  Tree,
  Typography
} from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import PageHeader from '@/components/PageHeader'
import Permission from '@/components/PermissionA5'
import { deleteSystemPermission } from '@/service/systemControl'
import { fetchEnumOptions } from '@/redux/components/enumOptionsSlice'
import { fetchSystemPermissionsList } from '@/redux/use/systemPermissionSlice'
import {
  buildPermissionTreeNodesForAntTree,
  collectAllTreeKeys,
  filterPermissionsByKeyword,
  getPermissionDepthInTree
} from '@/utils/rolePermissionTree'
import { formatDateTimeStr, renderTextStr } from '@/utils/renderUtils'
import PermissionAssignRolesDrawer from '@/views/systemControl/permissionList/share/PermissionAssignRolesDrawer'
import PermissionEditDrawer from '@/views/systemControl/permissionList/share/PermissionEditDrawer'

const { Text } = Typography

/** 第三层及以下不再允许「添加子权限」（顶级为第 1 层） */
const MAX_DEPTH_FOR_ADD_CHILD = 3

const breadcrumbList = [
  { breadcrumbName: '首页', href: '/client/commandCenter' },
  { breadcrumbName: '用户中心' },
  { breadcrumbName: '权限管理' }
]

function renderRolesBlock(roles) {
  if (!roles || !Array.isArray(roles) || roles.length === 0) {
    return <Text type="secondary">—</Text>
  }
  return (
    <Space size={[4, 4]} wrap>
      {roles.map((r, idx) => {
        const key = r?.id != null ? String(r.id) : `r-${idx}`
        const label =
          r?.name != null && r.name !== ''
            ? String(r.name)
            : r?.code != null && r.code !== ''
              ? String(r.code)
              : JSON.stringify(r)
        return (
          <Tag key={key}>
            {label}
          </Tag>
        )
      })}
    </Space>
  )
}

const mapStateToProps = (state) => ({
  permissionCodes: state.app.permissionCodes,
  systemPermission: state.use.systemPermission,
  enumOptions: state.components.enumOptions
})

const mapDispatchToProps = {
  fetchSystemPermissionsList,
  fetchEnumOptions
}

class PermissionListPage extends React.Component {
  state = {
    searchKeyword: '',
    selectedKeys: [],
    /** antd Tree 在 v5 下 defaultExpandAll 不可靠，用受控展开 */
    expandedKeys: []
  }

  componentDidMount() {
    this.refreshList()
    this.syncExpandedKeysFromTree()
    this.props.fetchEnumOptions({ key: 'PermissionType', dataType: 'option' })
      .unwrap()
      .catch((err) =>
        message.error(typeof err === 'string' ? err : '加载权限类型枚举失败')
      )
  }

  componentDidUpdate(prevProps, prevState) {
    const { list } = this.props.systemPermission
    const prevList = prevProps.systemPermission.list
    const { selectedKeys, searchKeyword } = this.state
    const listChanged = prevList !== list
    const searchChanged = prevState.searchKeyword !== searchKeyword

    if (listChanged || searchChanged) {
      this.syncExpandedKeysFromTree()
    }

    if (listChanged && selectedKeys.length) {
      const id = String(selectedKeys[0])
      const still = (list || []).some((x) => String(x.id) === id)
      if (!still) {
        this.setState({ selectedKeys: [] })
      }
    }
  }

  refreshList = () => {
    return this.props
      .fetchSystemPermissionsList({ includeRoles: true })
      .unwrap()
      .catch((err) => {
        message.error(typeof err === 'string' ? err : '加载权限列表失败')
      })
  }

  handleDeletePermission = async (record) => {
    const id = record?.id
    if (id == null) {
      message.error('缺少权限 id')
      return
    }
    try {
      await deleteSystemPermission(id)
      message.success('删除成功')
      this.setState({ selectedKeys: [] })
      await this.refreshList()
    } catch (e) {
      message.error(e?.message || e?.msg || '删除失败')
      throw e
    }
  }

  handleSearchChange = (e) => {
    this.setState({ searchKeyword: e.target.value })
  }

  /** 详情与 Redux `list` 同步：选中 id 在每次 render 时从全量 list 解析 */
  getSelectedRecord = () => {
    const { list } = this.props.systemPermission
    const { selectedKeys } = this.state
    if (!selectedKeys || !selectedKeys.length) {
      return null
    }
    const id = String(selectedKeys[0])
    return (list || []).find((x) => String(x.id) === id) || null
  }

  getTreeData = () => {
    const { list } = this.props.systemPermission
    const { searchKeyword } = this.state
    const filtered = filterPermissionsByKeyword(list || [], searchKeyword)
    return buildPermissionTreeNodesForAntTree(filtered)
  }

  /** 列表或筛选变化时默认展开全部节点（antd 5 需受控 expandedKeys） */
  syncExpandedKeysFromTree = () => {
    const treeData = this.getTreeData()
    this.setState({ expandedKeys: collectAllTreeKeys(treeData) })
  }

  handleTreeExpand = (keys) => {
    this.setState({ expandedKeys: keys })
  }

  handleTreeSelect = (keys) => {
    this.setState({ selectedKeys: keys || [] })
  }

  renderTreeTitle = (nodeData) => {
    const r = nodeData.record
    if (!r) return nodeData.title
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          paddingRight: 8
        }}
      >
        <span>{renderTextStr(r.name)}</span>
        {r.code ? (
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
            {r.code}
          </Text>
        ) : null}
      </span>
    )
  }

  renderDetailPanel = () => {
    const { list } = this.props.systemPermission
    const selectedRecord = this.getSelectedRecord()
    if (!selectedRecord) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="在左侧树中点击一个权限，可在此查看详情并编辑或添加子权限"
        />
      )
    }
    const canAddChildByDepth = getPermissionDepthInTree(list || [], selectedRecord) < MAX_DEPTH_FOR_ADD_CHILD
    return (
      <div>
        <Descriptions column={1} size="small" labelStyle={{ width: 96 }}>
          <Descriptions.Item label="ID">{selectedRecord.id}</Descriptions.Item>
          <Descriptions.Item label="名称">{renderTextStr(selectedRecord.name)}</Descriptions.Item>
          <Descriptions.Item label="编码">
            <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
              {renderTextStr(selectedRecord.code)}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="类型">{renderTextStr(selectedRecord.type)}</Descriptions.Item>
          <Descriptions.Item label="备注">{renderTextStr(selectedRecord.remark)}</Descriptions.Item>
          <Descriptions.Item label="关联角色">{renderRolesBlock(selectedRecord.roles)}</Descriptions.Item>
          <Descriptions.Item label="创建时间">{formatDateTimeStr(selectedRecord.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="更新时间">{formatDateTimeStr(selectedRecord.updatedAt)}</Descriptions.Item>
        </Descriptions>
        <div style={{ marginTop: 16 }}>
          <Space wrap>
            <Permission
              rootPath="systemControl"
              dataRes={[canAddChildByDepth]}
            >
              <span pathitem={['permissionList', 'assignRoles']} checkarray={['buttonCodes']}>
                <PermissionAssignRolesDrawer record={selectedRecord} onSuccess={this.refreshList}>
                  <Button>为权限分配角色</Button>
                </PermissionAssignRolesDrawer>
              </span>
              <span pathitem={['permissionList', 'edit']} checkarray={['buttonCodes']}>
                <PermissionEditDrawer
                  actType="edit"
                  record={selectedRecord}
                  defaultParentId={null}
                  onSuccess={this.refreshList}
                >
                  <Button type="primary">编辑当前权限</Button>
                </PermissionEditDrawer>
              </span>
              <span pathitem={['permissionList', 'addChild']} checkarray={['buttonCodes', 'dataRes']}>
                <PermissionEditDrawer
                  actType="create"
                  defaultParentId={selectedRecord.id}
                  onSuccess={this.refreshList}
                >
                  <Button>添加子权限</Button>
                </PermissionEditDrawer>
              </span>
              <span pathitem={['permissionList', 'delete']} checkarray={['buttonCodes']}>
                <Popconfirm
                  title="确定删除该权限？存在子节点时请先删除子权限，或按后端规则处理。"
                  okText="删除"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => this.handleDeletePermission(selectedRecord)}
                >
                  <Button danger>删除</Button>
                </Popconfirm>
              </span>
            </Permission>
          </Space>
        </div>
      </div>
    )
  }

  render() {
    const { fetching } = this.props.systemPermission
    const { searchKeyword, selectedKeys, expandedKeys } = this.state
    const treeData = this.getTreeData()
    const headerAction = (
      <Permission rootPath="systemControl">
        <span pathitem={['permissionList', 'createTop']} checkarray={['buttonCodes']}>
          <PermissionEditDrawer actType="create" onSuccess={this.refreshList}>
            <Button type="primary" icon={<PlusOutlined />}>
              新建顶级权限
            </Button>
          </PermissionEditDrawer>
        </span>
      </Permission>
    )
    return (
      <div>
        <PageHeader breadcrumbList={breadcrumbList} extra={headerAction} />
        <div className="i2-container">
          <Card className="i2-card-page">
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={10} xl={9}>
                <div style={{ marginBottom: 10 }}>
                  <Input.Search
                    allowClear
                    placeholder="筛选名称、编码、类型…"
                    value={searchKeyword}
                    onChange={this.handleSearchChange}
                  />
                </div>
                <Spin spinning={fetching}>
                  <div
                    style={{
                      border: '1px solid rgba(0,0,0,0.06)',
                      borderRadius: 8,
                      padding: '8px 8px 4px',
                      minHeight: 420,
                      maxHeight: 'calc(100vh - 280px)',
                      overflow: 'auto'
                    }}
                  >
                    {treeData.length === 0 && !fetching ? (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />
                    ) : (
                      <Tree
                        blockNode
                        showLine
                        treeData={treeData}
                        expandedKeys={expandedKeys}
                        onExpand={this.handleTreeExpand}
                        selectedKeys={selectedKeys}
                        onSelect={this.handleTreeSelect}
                        titleRender={this.renderTreeTitle}
                      />
                    )}
                  </div>
                </Spin>
              </Col>
              <Col xs={24} lg={14} xl={15}>
                <Card size="small" title="权限详情" bodyStyle={{ minHeight: 420 }}>
                  {this.renderDetailPanel()}
                </Card>
              </Col>
            </Row>
          </Card>
        </div>
      </div>
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PermissionListPage)
